import { Calculator, Users2, User, Users, Table2, HandCoins, Lock } from "lucide-react";
import { Badge, Card, SectionHeading } from "./ui";
import type { PricingPayload } from "../lib/api";
import { formatINR, percent } from "../lib/format";

// PUBLIC price combinations — the organizer's confirmed budget model:
//   expenditure ≈ ₹1,000/person · senior ₹1,290 + junior ₹790 = ₹2,080 per unit
//   leftover refunded to students by contribution-share ratio.
// Vendor per-plate rates are committee-only and deliberately absent here.

function StatCard({
  icon: Icon,
  title,
  value,
  rows,
  footer,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string;
  rows: Array<{ label: string; value: string }>;
  footer?: React.ReactNode;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-center gap-2.5">
        <Icon className="h-5 w-5 text-wandor-prompt" aria-hidden="true" />
        <h3 className="text-sm font-medium uppercase tracking-[0.16em] text-white/70">{title}</h3>
      </div>
      <p className="mt-3 text-3xl font-semibold text-white">{value}</p>
      <dl className="mt-4 space-y-2 border-t border-white/10 pt-4">
        {rows.map((r) => (
          <div key={r.label} className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="text-white/55">{r.label}</dt>
            <dd className="font-medium text-white">{r.value}</dd>
          </div>
        ))}
      </dl>
      {footer && <div className="mt-4 text-xs leading-relaxed text-white/45">{footer}</div>}
    </Card>
  );
}

export default function PricingBreakdown({ pricing }: { pricing: PricingPayload }) {
  const { contributions: c, perPersonExpensePaise, refund, scenarios } = pricing;
  const awaiting = c.seniorAwaiting || c.juniorAwaiting || perPersonExpensePaise <= 0;

  return (
    <section id="pricing" className="mx-auto max-w-shell px-5 pb-24 md:px-10">
      <SectionHeading
        eyebrow="[ PRICE COMBINATIONS ]"
        title="What each student contributes — and what comes back"
        description="These are the committee's planning figures, computed server-side. Contribution amounts and per-person expenditure are kept separate from vendor pricing, which is held privately by the committee."
        align="left"
      />

      {awaiting && (
        <div className="mt-6 rounded-xl border border-amber-300/30 bg-amber-200/[0.06] p-4">
          <p className="text-sm text-amber-200">
            Some figures are still being finalised by the committee and will appear here once confirmed.
          </p>
        </div>
      )}

      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <StatCard
          icon={Calculator}
          title="Per-person expenditure (planning)"
          value={perPersonExpensePaise > 0 ? formatINR(perPersonExpensePaise) : "—"}
          rows={[
            { label: "Covers", value: "Dinner + DJ + decoration + other amenities" },
            {
              label: "Status",
              value: "Planning estimate — committee-set",
            },
          ]}
          footer="This is the planned cost of hosting one attendee. It is not a bill — the committee publishes actuals after the event."
        />
        <StatCard
          icon={User}
          title="One junior"
          value={c.juniorAwaiting ? "—" : formatINR(c.juniorPaise)}
          rows={[
            { label: "Contribution per junior", value: c.juniorAwaiting ? "Awaiting confirmation" : formatINR(c.juniorPaise) },
            { label: "Share of one unit pair", value: refund.juniorShareBps / 100 + "%" },
          ]}
          footer="A junior's contribution is part of the ₹2,080 unit pair below."
        />
        <StatCard
          icon={Users2}
          title="One senior"
          value={c.seniorAwaiting ? "—" : formatINR(c.seniorPaise)}
          rows={[
            { label: "Contribution per senior", value: c.seniorAwaiting ? "Awaiting confirmation" : formatINR(c.seniorPaise) },
            { label: "Share of one unit pair", value: refund.seniorShareBps / 100 + "%" },
          ]}
          footer="A senior's contribution is part of the ₹2,080 unit pair below."
        />
      </div>

      {/* Combined pair */}
      <div className="mt-6">
        <StatCard
          icon={Users}
          title="One junior + one senior (unit pair)"
          value={c.pairPaise > 0 ? formatINR(c.pairPaise) : "—"}
          rows={[
            {
              label: "Combined pair contribution",
              value: c.pairPaise > 0 ? `${formatINR(c.juniorPaise)} + ${formatINR(c.seniorPaise)} = ${formatINR(c.pairPaise)}` : "—",
            },
            { label: "Formula", value: "Pair = junior + senior" },
          ]}
          footer={
            <>
              Combined pair cost = one junior&apos;s contribution + one senior&apos;s contribution.
              Contributions are separate from vendor costs — the committee settles vendors directly.
            </>
          }
        />
      </div>

      {/* Refund policy */}
      <div className="mt-6">
        <Card className="p-6">
          <div className="flex items-center gap-2.5">
            <HandCoins className="h-5 w-5 text-wandor-prompt" aria-hidden="true" />
            <h3 className="text-sm font-medium uppercase tracking-[0.16em] text-white/70">
              What happens to the money left over
            </h3>
          </div>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-white/65">{refund.note}</p>
          <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-white/10 p-4">
              <p className="text-xs uppercase tracking-wider text-white/50">Senior share of one unit</p>
              <p className="mt-1 text-2xl font-semibold text-white">{percent(refund.seniorShareBps / 100)}</p>
              <p className="mt-1 text-xs text-white/45">= ₹1,290 ÷ ₹2,080 of every refunded unit</p>
            </div>
            <div className="rounded-lg border border-white/10 p-4">
              <p className="text-xs uppercase tracking-wider text-white/50">Junior share of one unit</p>
              <p className="mt-1 text-2xl font-semibold text-white">{percent(refund.juniorShareBps / 100)}</p>
              <p className="mt-1 text-xs text-white/45">= ₹790 ÷ ₹2,080 of every refunded unit</p>
            </div>
          </div>
          <p className="mt-4 text-xs leading-relaxed text-white/45">
            This website only shows the calculation. No money is collected or transferred here — the
            committee handles all payments and refunds offline.
          </p>
        </Card>
      </div>

      {/* Scenario comparison */}
      <div className="mt-10">
        <div className="flex items-center gap-2.5">
          <Table2 className="h-5 w-5 text-wandor-prompt" aria-hidden="true" />
          <h3 className="text-sm font-medium uppercase tracking-[0.16em] text-white/70">
            Attendance scenarios
          </h3>
        </div>
        <div className="mt-4 overflow-x-auto rounded-xl border border-white/10">
          <table className="w-full min-w-[680px] text-left text-sm">
            <thead>
              <tr className="border-b border-white/10 text-xs uppercase tracking-wider text-white/50">
                <th className="px-4 py-3 font-medium">Scenario</th>
                <th className="px-4 py-3 font-medium">Seniors</th>
                <th className="px-4 py-3 font-medium">Juniors</th>
                <th className="px-4 py-3 font-medium">Attendees</th>
                <th className="px-4 py-3 font-medium">Expenditure</th>
                <th className="px-4 py-3 font-medium">Contributions</th>
                <th className="px-4 py-3 font-medium">Leftover</th>
              </tr>
            </thead>
            <tbody>
              {scenarios.map((s) => (
                <tr key={s.key} className="border-b border-white/5 last:border-0">
                  <td className="px-4 py-3 text-white">
                    {s.label}
                    {s.isPlanningScenario && (
                      <Badge tone="warning">
                        <span className="ml-1">planning scenario</span>
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-white/80">{s.seniors}</td>
                  <td className="px-4 py-3 tabular-nums text-white/80">{s.juniors}</td>
                  <td className="px-4 py-3 tabular-nums text-white/80">{s.total}</td>
                  <td className="px-4 py-3 tabular-nums text-white/80">{formatINR(s.expensePaise)}</td>
                  <td className="px-4 py-3 tabular-nums text-white/80">{formatINR(s.contributionIncomePaise)}</td>
                  <td
                    className={`px-4 py-3 tabular-nums font-medium ${
                      s.gapPaise >= 0 ? "text-emerald-300" : "text-amber-300"
                    }`}
                  >
                    {s.gapPaise >= 0 ? "" : "−"}
                    {formatINR(Math.abs(s.gapPaise))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-white/45">
          Expenditure = attendees × {formatINR(perPersonExpensePaise)} per person. Contributions = seniors ×{" "}
          {formatINR(c.seniorPaise)} + juniors × {formatINR(c.juniorPaise)}. The 30/30 row is an
          illustrative planning scenario, not confirmed attendance. Positive leftover figures are
          refunded to students by share ratio — they are not profit.
        </p>
      </div>

      {/* Committee-only vendor pricing */}
      <div className="mt-10">
        <Card className="flex items-start gap-3 p-5">
          <Lock className="mt-0.5 h-5 w-5 shrink-0 text-white/50" aria-hidden="true" />
          <div>
            <p className="text-sm font-medium text-white">Vendor per-plate pricing — committee only</p>
            <p className="mt-1 text-xs leading-relaxed text-white/50">
              The restaurant&apos;s per-plate quotation and vendor cost breakdowns are managed privately in
              the committee dashboard and are not published on this site. The figures shown above are
              the planning model the committee has approved for students.
            </p>
          </div>
        </Card>
      </div>
    </section>
  );
}
